/**
 * ThemeToggle Component
 *
 * Theme switcher with:
 * - Dark mode toggle (light/dark)
 * - Alex mode toggle (if applicable)
 * - Icons for sun/moon
 * - Smooth transitions
 * - Visual feedback
 *
 * @module ThemeToggle
 */

import React from 'react';
import { IconSun, IconMoon, IconSparkles } from '@tabler/icons-react';
import { useTheme } from '../../../contexts/theme/ThemeContext';

/**
 * ThemeToggle - Theme controls
 */
const ThemeToggle = () => {
  const { isDarkMode, isAlexMode, toggleDarkMode, toggleAlexMode } = useTheme();

  return (
    <div className="space-y-2">
      {/* Dark Mode Toggle */}
      <button
        type="button"
        onClick={toggleDarkMode}
        className={`
          w-full flex items-center justify-between px-4 py-3
          bg-glass-bg border border-glass-border
          hover:bg-glass-bg-hover hover:border-glass-border-hover
          focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2
          focus:ring-offset-surface-0
        `}
        aria-label={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}
      >
        <span className="flex items-center gap-2 text-sm font-medium text-text-secondary">
          {isDarkMode ? (
            <>
              <IconMoon className="w-5 h-5" aria-hidden="true" />
              <span>Dark Mode</span>
            </>
          ) : (
            <>
              <IconSun className="w-5 h-5" aria-hidden="true" />
              <span>Light Mode</span>
            </>
          )}
        </span>

        {/* Toggle Switch */}
        <div
          className={`
            relative w-11 h-6
            ${isDarkMode ? 'bg-accent' : 'bg-surface-4'}
          `}
        >
          <div
            className={`
              absolute top-1 left-1 w-4 h-4 bg-white
              ${isDarkMode ? 'translate-x-5' : 'translate-x-0'}
            `}
          />
        </div>
      </button>

      {/* Alex Mode Toggle (if needed) */}
      {toggleAlexMode && (
        <button
          type="button"
          onClick={toggleAlexMode}
          className={`
            w-full flex items-center justify-between px-4 py-3
            bg-glass-bg border border-glass-border
            hover:bg-glass-bg-hover hover:border-glass-border-hover
            focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2
            focus:ring-offset-surface-0
          `}
          aria-label={`${isAlexMode ? 'Disable' : 'Enable'} Alex mode`}
        >
          <span className="flex items-center gap-2 text-sm font-medium text-text-secondary">
            <IconSparkles className="w-5 h-5" aria-hidden="true" />
            <span>Alex Mode</span>
          </span>

          {/* Toggle Switch */}
          <div
            className={`
              relative w-11 h-6
              ${isAlexMode ? 'bg-accent' : 'bg-surface-4'}
            `}
          >
            <div
              className={`
                absolute top-1 left-1 w-4 h-4 bg-white
                ${isAlexMode ? 'translate-x-5' : 'translate-x-0'}
              `}
            />
          </div>
        </button>
      )}
    </div>
  );
};

export default React.memo(ThemeToggle);
